import http from "../helpers/http";
import db from "../helpers/db";
import { clearAuthenticatedBodyShell } from "../helpers/authShell";
import { initWebSocket, disconnectWebSocket } from "../websocket";
import { isMarketplacePlace } from "../helpers/marketplacePlace";
import { isSubscriptionBlockedForBilling } from "../helpers/platformSubscription";

export default {
    namespaced: true,
    state: {
        user: db.user,
        token: db.token,
        place: db.place,
        professionals: db.professionals,
        certifier: db.certifier,
        permissions: db.permissions,
        terminal: db.terminal,
        platformSubscription: db.platformSubscription,
        platformModuleSlugs: db.platformSubscription?.module_slugs ?? [],
        platformFeatureSlugs: db.platformSubscription?.feature_slugs ?? [],
        mustSetupTwoFactor: db.mustSetupTwoFactor,
    },
    mutations: {
        setUser(state, user) {
            state.user = db.storeUser(user);
        },
        setToken(state, token) {
            state.token = db.storeToken(token);
        },
        setPlace(state, place) {
            const previousPlaceId = state.place?.id_place;
            state.place = db.storePlace(place);
            state.terminal = db.terminal;

            if (place?.id_place != null && previousPlaceId !== place.id_place) {
                state.permissions = [];
                state.platformSubscription = null;
                state.platformModuleSlugs = [];
                state.platformFeatureSlugs = [];
                db.storePermissions([]);
                db.storePlatformSubscription(null);
            }
        },
        setProfessionals(state, professionals) {
            state.professionals = db.storeProfessionals(professionals);
        },
        setCertifier(state, certifier) {
            state.certifier = db.storeCertifier(certifier);
        },
        setPermissions(state, permissions) {
            state.permissions = db.storePermissions(permissions);
        },
        setTerminal(state, terminal) {
            state.terminal = db.storeTerminal(terminal);
        },
        removeTerminal(state) {
            db.removeTerminal();
            state.terminal = null;
        },
        setMustSetupTwoFactor(state, value) {
            state.mustSetupTwoFactor = db.storeMustSetupTwoFactor(value);
        },
        setPlatformSubscription(state, subscription) {
            if (subscription == null) {
                state.platformSubscription = db.storePlatformSubscription(null);
                state.platformModuleSlugs = [];
                state.platformFeatureSlugs = [];

                return;
            }

            const current = state.platformSubscription ?? {};
            const merged = {
                ...current,
                ...subscription,
            };

            if (!Array.isArray(subscription.module_slugs)) {
                merged.module_slugs = current.module_slugs ?? state.platformModuleSlugs ?? [];
            }

            if (!Array.isArray(subscription.feature_slugs)) {
                merged.feature_slugs = current.feature_slugs ?? state.platformFeatureSlugs ?? [];
            }

            state.platformSubscription = db.storePlatformSubscription(merged);
            state.platformModuleSlugs = merged.module_slugs ?? [];
            state.platformFeatureSlugs = merged.feature_slugs ?? [];

            if (!state.place) {
                return;
            }

            state.place = db.storePlace({
                ...state.place,
                platform_subscription_status: merged.status ?? state.place.platform_subscription_status,
                platform_subscription_is_blocked: merged.is_blocked ?? state.place.platform_subscription_is_blocked,
                platform_subscription_can_use: merged.can_use_platform ?? state.place.platform_subscription_can_use,
                platform_subscription_exempt: merged.exempt ?? state.place.platform_subscription_exempt,
                platform_subscription_has_subscription: merged.has_subscription ?? state.place.platform_subscription_has_subscription,
                place_is_marketplace: merged.is_marketplace != null
                    ? Boolean(merged.is_marketplace)
                    : state.place.place_is_marketplace,
            });
        },
        clearSession(state) {
            db.clearSession();
            clearAuthenticatedBodyShell();
            state.user = null;
            state.token = null;
            state.place = null;
            state.professionals = null;
            state.certifier = null;
            state.permissions = [];
            state.terminal = null;
            state.platformSubscription = null;
            state.platformModuleSlugs = [];
            state.platformFeatureSlugs = [];
            state.mustSetupTwoFactor = false;
            db.storePlatformSubscription(null);
        },
    },
    actions: {
        selectPlace(context, place) {
            context.commit('setPlace', place);

            return Promise.all([
                context.dispatch('loadPlatformSubscription'),
                context.dispatch('loadPermissions'),
                context.dispatch('loadProfessionals'),
                context.dispatch('queue/fetchPatients', null, { root: true }),
            ]);
        },
        refreshEntitlements(context) {
            if (!context.state.place) {
                return Promise.resolve();
            }

            return Promise.all([
                context.dispatch('loadPlatformSubscription'),
                context.dispatch('loadPermissions'),
            ]);
        },
        loginUsingToken(context, token) {
            if (!context.state.token)
                context.commit('setToken', token);

            return http.get('/users/me').then(response => {
                context.commit('setUser', response.data);
                // Inicializa websocket após autenticação
                initWebSocket();
                return response;
            });
        },
        bootstrapFromToken(context, token) {
            disconnectWebSocket();
            context.commit('clearSession');
            context.commit('setToken', token);

            return http.get('/users/me').then(response => {
                context.commit('setUser', response.data);
                initWebSocket();
                return context.dispatch('loadPlaces', response.data);
            });
        },
        login(context, auth) {
            return http.post('/auth/login', {
                user_email: auth.username,
                user_password: auth.password
            }).then((response) => {
                if (response.data.two_factor_required) {
                    const error = new Error('two_factor_required');
                    error.pendingLoginToken = response.data.pending_login_token;
                    error.userId = response.data.user_id;
                    throw error;
                }

                return context.dispatch('finalizeLogin', { ...response.data, deferPlaceLoading: auth.deferPlaceLoading });
            });
        },
        completeTwoFactorLogin(context, payload) {
            return http.post('/auth/login/two-factor', payload).then((response) => {
                return context.dispatch('finalizeLogin', response.data);
            });
        },
        finalizeLogin(context, data) {
            context.commit('setUser', data.user);
            context.commit('setToken', data.token);
            context.commit('setMustSetupTwoFactor', Boolean(data.must_setup_two_factor));
            initWebSocket();

            if (data.must_setup_two_factor || data.deferPlaceLoading) {
                return Promise.resolve(data);
            }

            return context.dispatch('loadPlaces', data.user);
        },
        loadPlaces(context, user) {
            return http.get(`/users/${user.id_user}/places`).then(async (res) => {
                if (res.data.length !== 1) {
                    window.location.href = '/places';
                } else {
                    const place = res.data[0];
                    if (isMarketplacePlace(place)) {
                        window.location.href = '/places?marketplace_redirect=1';
                        return res;
                    }
                    if (isSubscriptionBlockedForBilling(place)) {
                        window.location.href = '/subscription/overdue';
                        return res;
                    }
                    await context.dispatch('selectPlace', place);

                    return res;
                }

                return res;
            });
        },
        loadProfessionals(context) {
            if (!context.state.place)
                return;

            return http.get(`/places/${context.state.place.id_place}/professionals`).then((response) => {
                context.commit('setProfessionals', response.data);
                return response;
            });
        },
        loadCertifiers(context) {
            if (!context.state.certifiers)
                return;

            return http.get(`/certifiers`).then((response) => {
                context.commit('setCertifiers', response.data);
                return response;
            });
        },
        loadPermissions(context) {
            if (!context.state.place) {
                return Promise.resolve();
            }

            return http.get(`/places/${context.state.place.id_place}/users/${context.state.user.id_user}/permissions`).then((response) => {
                context.commit('setPermissions', response.data);
                return response;
            }).catch((error) => {
                // 402: assinatura do local em atraso. Não é falta de acesso —
                // o local entra assim mesmo e o guard leva para a assinatura,
                // onde dá para regularizar ou trocar de unidade.
                if (error.response?.status === 402 && error.response?.data?.subscription_blocked) {
                    context.commit('setPermissions', []);

                    return null;
                }

                return Promise.reject(error);
            });
        },
        loadPlatformSubscription(context) {
            if (!context.state.place)
                return;

            return http.get(`/places/${context.state.place.id_place}/platform-subscription`).then((response) => {
                context.commit('setPlatformSubscription', response.data);
                return response;
            }).catch((error) => {
                if (error.response?.status === 404) {
                    context.commit('setPlatformSubscription', {
                        has_subscription: false,
                        module_slugs: [],
                        feature_slugs: [],
                    });
                }
            });
        },
        recoverPassword(context, username) {
            return http.post('/auth/password/link', {cpf: username});
        },
        recoverPasswordCode(context, username) {
            return http.post('/auth/password/code', {cpf: username});
        },
        validateCode(context, data) {
            return http.post(`/auth/password/validate-code`, {user_cpf: data.user_cpf, code: data.code});
        },
        logout(context) {
            disconnectWebSocket();
            context.commit('clearSession');

            http.post('/auth/logout').catch(() => {});

            return Promise.resolve();
        },
        async validateTerminal(context) {
            if (!context.state.terminal) return;
            try {
                await http.post(`/places/${context.state.place.id_place}/terminals/validate`, {
                    token: context.state.place?.token_totem,
                    terminal: context.state.terminal?.terminal_hash
                });
            } catch (error) {
                if (error.response?.status === 403) {
                    context.commit('removeTerminal');
                }
            }
        },
    },
    getters: {
        isAuth(state) {
            return state.user != null
                && state.token != null
                && state.token !== 'null'
                && state.token !== '';
        }
    },
}